import { updateDesignSystemRoot } from "@/shared";
import type { TDesignSystemRoot } from "@/shared/types";
import type {
  ChangeEntityLayoutPayloadType,
  CreateGenericPayloadType,
  CreateInsertIntoCellPayloadType,
  CreateMoveInCellElementPayloadType,
  CreatePasteCellsPayloadType,
  CreatePayloadType,
  DeletePayloadType,
  DesignSystemPayloadType,
  EditManyPayloadType,
  EditPayloadType,
  ReorderPayloadType,
  StoryStateType,
} from "../types";
import { applyInCellElementMove, undoInCellElementMove } from "./apply-in-cell-element-move";
import handleChangeEntityLayout from "./handle-change-entity-layout";
import handleDesignSystem from "./handle-design-system";
import { pruneOrphanedCellChildren } from "./prune-orphaned-cell-children";
import { updateTabsDefault } from "./updateEntityChildrenSettings";

export const undoHistoryChange = (state: StoryStateType, payload: unknown) => {
  if (payload && typeof payload === "object" && "action" in payload) {
    switch (payload.action) {
      case "edit":
        handleUndoEditAction(state, payload as EditPayloadType);
        break;
      case "edit-many":
        handleUndoEditManyAction(state, payload as EditManyPayloadType);
        break;
      case "delete":
        handleUndoDeleteAction(state, payload as DeletePayloadType);
        break;
      case "reorder":
        handleUndoReorderAction(state, payload as ReorderPayloadType);
        break;
      case "create":
        handleUndoCreateAction(state, payload as CreatePayloadType);
        break;
      case "design-system":
        handleUndoDesignSystemAction(state, payload as DesignSystemPayloadType);
        break;
      case "change-entity-layout":
        handleUndoChangeEntityLayoutAction(
          state,
          payload as ChangeEntityLayoutPayloadType,
        );
        break;
      case "ai-checkpoint":
        // No-op marker frame. Used by the AI agent to bracket
        // multi-dispatch operations so they can be undone as a
        // single user-facing action.
        break;
    }
  }
};

export const redoHistoryChange = (state: StoryStateType, payload: unknown) => {
  if (payload && typeof payload === "object" && "action" in payload) {
    switch (payload.action) {
      case "edit":
        handleRedoEditAction(state, payload as EditPayloadType);
        break;
      case "edit-many":
        handleRedoEditManyAction(state, payload as EditManyPayloadType);
        break;
      case "delete":
        handleRedoDeleteAction(state, payload as DeletePayloadType);
        break;
      case "reorder":
        handleRedoReorderAction(state, payload as ReorderPayloadType);
        break;
      case "create":
        handleRedoCreateAction(state, payload as CreatePayloadType);
        break;
      case "design-system":
        handleRedoDesignSystemAction(state, payload as DesignSystemPayloadType);
        break;
      case "change-entity-layout":
        handleRedoChangeEntityLayoutAction(
          state,
          payload as ChangeEntityLayoutPayloadType,
        );
        break;
      case "ai-checkpoint":
        // No-op marker frame on redo too.
        break;
    }
  }
};

// ========== UNDO ==========
const handleUndoEditAction = (
  state: StoryStateType,
  payload: EditPayloadType,
) => {
  const entity = state.flatContentLayers[payload.id];
  if (!entity) return;
  const {
    currentSettings,
    childCurrentSettings,
    relativeCurrentEntitiesSettings,
    payload: editPayload,
  } = payload;

  if (typeof currentSettings === "object" && currentSettings !== null) {
    for (const key in currentSettings) {
      entity.settings[key] = currentSettings[key];
    }
  }

  if (
    editPayload.children &&
    typeof childCurrentSettings === "object" &&
    childCurrentSettings !== null
  ) {
    for (const childId in childCurrentSettings) {
      const child = state.flatContentLayers[childId];
      if (!child) {
        console.error(
          `Child with id ${childId} not found in flatContentLayers`,
        );
        continue;
      }
      for (const key in childCurrentSettings[childId]) {
        child.settings[key] = childCurrentSettings[childId][key];
      }
    }
  }

  // Re-register children removed by the orphan-prune invariant, splicing the
  // roots back into table.childIds at their pre-prune positions (ascending so
  // earlier splices don't shift later targets).
  if (payload.prunedChildren) {
    const { entities: prunedEntities, indexes } = payload.prunedChildren;
    state.flatContentLayers = { ...state.flatContentLayers, ...prunedEntities };
    const table = state.flatContentLayers[payload.id];
    if (table) {
      Object.entries(indexes)
        .sort((a, b) => a[1] - b[1])
        .forEach(([rootId, index]) => {
          table.childIds.splice(
            Math.min(index, table.childIds.length),
            0,
            rootId,
          );
        });
    }
  }

  // =========== TABS PIN DEFAULT ===========
  if (
    typeof relativeCurrentEntitiesSettings === "object" &&
    relativeCurrentEntitiesSettings !== null
  ) {
    for (const entityId in relativeCurrentEntitiesSettings) {
      const entity = state.flatContentLayers[entityId];
      if (!entity) {
        console.error(
          `Entity with id ${entityId} not found in flatContentLayers`,
        );
        continue;
      }
      for (const key in relativeCurrentEntitiesSettings[entityId]) {
        entity.settings[key] = relativeCurrentEntitiesSettings[entityId][key];
      }
    }
  }
  // =========== TABS PIN DEFAULT ===========
};

const handleUndoEditManyAction = (
  state: StoryStateType,
  payload: EditManyPayloadType,
) => {
  const { current } = payload;
  Object.entries(current).forEach(([id, payload]) => {
    const entity = state.flatContentLayers[id];
    if (!entity) return;
    Object.keys(payload).forEach((key) => {
      entity.settings[key] = payload[key];
    });
  });
  // Atomic undo (Gap 2 / KNOWN LIMITATION fix, see repeatable.thunk.ts):
  // when this edit-many frame was folded together with a settings.fs write
  // (e.g. make/unmark-repeatable's fs.repeatables definition), restore the
  // PRIOR fs object so one Ctrl+Z reverses the entity edits AND the
  // definition write as a single, atomic step.
  if (payload.fsPatch && state.story) {
    state.story.settings.fs = payload.fsPatch.current;
  }
};

const handleUndoDeleteAction = (
  state: StoryStateType,
  payload: DeletePayloadType,
) => {
  const { rootId, index, entities } = payload;
  Object.keys(entities).forEach((entityId) => {
    state.flatContentLayers[entityId] = entities[entityId];
  });
  const root = state.flatContentLayers[rootId];
  const parent = state.flatContentLayers[root.parentId || ""];
  if (parent) {
    parent.childIds.splice(index, 0, rootId);
    if (!payload.broadcast) state.activeEntityId = rootId;
  }
};

const handleUndoReorderAction = (
  state: StoryStateType,
  payload: ReorderPayloadType,
) => {
  const { id, currentParentId, updatedParentId, currentIndex, updatedIndex } =
    payload.payload;

  const entity = state.flatContentLayers[id];
  if (!entity) return;
  const parent = state.flatContentLayers[currentParentId];
  if (!parent) return;
  if (currentParentId === updatedParentId) {
    parent.childIds.splice(updatedIndex, 1);
    parent.childIds.splice(currentIndex, 0, id);
  } else {
    const updatedParent = state.flatContentLayers[updatedParentId];
    if (!updatedParent) return;
    updatedParent.childIds.splice(updatedIndex, 1);
    parent.childIds.splice(currentIndex, 0, id);
    entity.parentId = currentParentId;
  }
};

const handleUndoCreateAction = (
  state: StoryStateType,
  payload: CreatePayloadType,
) => {
  const {
    parentId,
    initialActiveId,
    initialActiveThumbnailId,
    entities,
    index,
    type,
  } = payload;
  if (type === "narrator-dialog")
    return handleNarratorDialogUndoCreateAction(
      state,
      payload as CreateGenericPayloadType,
    );
  if (type === "insert-into-cell")
    return handleInsertIntoCellUndoCreateAction(
      state,
      payload as CreateInsertIntoCellPayloadType,
    );
  if (type === "move-in-cell-element")
    return handleMoveInCellElementUndoCreateAction(
      state,
      payload as CreateMoveInCellElementPayloadType,
    );
  if (type === "paste-cells")
    return handlePasteCellsUndoCreateAction(
      state,
      payload as CreatePasteCellsPayloadType,
    );
  const parent = state.flatContentLayers[parentId];
  if (!parent) return;
  if (!payload.broadcast) {
    state.activeEntityId = initialActiveId;
    // Without this the rail's pointer is left dangling and falls back to slide 0.
    if (initialActiveThumbnailId != null) {
      state.activeThumbnailId = initialActiveThumbnailId;
    }
  }
  parent.childIds.splice(index, 1);
  for (const entityId in entities) {
    delete state.flatContentLayers[entityId];
  }
};

// Undo of a Tables 2.0 in-cell insert: drop the inserted roots from
// table.childIds, delete their subtrees and restore the cell's prior text.
const handleInsertIntoCellUndoCreateAction = (
  state: StoryStateType,
  payload: CreateInsertIntoCellPayloadType,
) => {
  const { entities, initialActiveId, rootIds, tableEntityId, cellId } = payload;
  const table = state.flatContentLayers[tableEntityId || ""];
  if (!table || !rootIds) return;
  const rootIdSet = new Set(rootIds);
  table.childIds = table.childIds.filter((id) => !rootIdSet.has(id));
  for (const entityId in entities) {
    delete state.flatContentLayers[entityId];
  }
  if (payload.priorContent !== undefined) {
    const tableData = table.settings.tableData as
      | Array<{ id: string; content: string }>
      | undefined;
    const cell = tableData?.find((c) => c.id === cellId);
    if (cell) cell.content = payload.priorContent;
  }
  if (!payload.broadcast) state.activeEntityId = initialActiveId;
};

const handleInsertIntoCellRedoCreateAction = (
  state: StoryStateType,
  payload: CreateInsertIntoCellPayloadType,
) => {
  const { entities, index, rootIds, tableEntityId, cellId, content } = payload;
  const table = state.flatContentLayers[tableEntityId || ""];
  if (!table || !rootIds) return;
  state.flatContentLayers = { ...state.flatContentLayers, ...entities };
  const at = Math.min(index, table.childIds.length);
  table.childIds.splice(at, 0, ...rootIds);
  if (content !== undefined) {
    const tableData = table.settings.tableData as
      | Array<{ id: string; content: string }>
      | undefined;
    const cell = tableData?.find((c) => c.id === cellId);
    if (cell) cell.content = content;
  }
  if (!payload.broadcast) {
    state.activeEntityId = rootIds[rootIds.length - 1];
  }
};

// Undo of a Tables 2.0 in-cell move (Task 5): move the element back to its
// prior container/cell/index via Task 3's pure undo, then reverse any
// text→RichText promotion that ran as part of the same step.
const handleMoveInCellElementUndoCreateAction = (
  state: StoryStateType,
  payload: CreateMoveInCellElementPayloadType,
) => {
  const {
    elementId,
    fromContainerId,
    fromCellId,
    fromIndex,
    initialActiveId,
    promoteRootIds,
    priorContent,
    toContainerId,
    toCellId,
    entities,
  } = payload;
  undoInCellElementMove(state, {
    elementId,
    fromContainerId,
    fromCellId,
    fromIndex,
  });

  if (promoteRootIds?.length && toContainerId && toCellId) {
    const table = state.flatContentLayers[toContainerId];
    if (table) {
      const rootIdSet = new Set(promoteRootIds);
      table.childIds = table.childIds.filter((id) => !rootIdSet.has(id));
      for (const id of promoteRootIds) {
        delete state.flatContentLayers[id];
      }
      // Also drop any other entities recorded on the history frame for the
      // promotion (today just the RichText root).
      for (const entityId in entities) {
        if (rootIdSet.has(entityId)) continue;
        delete state.flatContentLayers[entityId];
      }
      if (priorContent !== undefined) {
        const tableData = table.settings.tableData as
          | Array<{ id: string; content: string }>
          | undefined;
        const cell = tableData?.find((c) => c.id === toCellId);
        if (cell) cell.content = priorContent;
      }
    }
  }

  if (!payload.broadcast) state.activeEntityId = initialActiveId;
};

const handleMoveInCellElementRedoCreateAction = (
  state: StoryStateType,
  payload: CreateMoveInCellElementPayloadType,
) => {
  const {
    elementId,
    toContainerId,
    toCellId,
    index,
    promoteRootIds,
    entities,
    content,
    priorContent,
  } = payload;

  if (
    promoteRootIds?.length &&
    toCellId &&
    entities &&
    Object.keys(entities).length > 0
  ) {
    // Re-apply promotion: splice RichText back and clear cell content.
    const table = state.flatContentLayers[toContainerId];
    if (table) {
      state.flatContentLayers = { ...state.flatContentLayers, ...entities };
      // Find absolute index: front of this cell's siblings (same as promote).
      const siblings = table.childIds.filter(
        (id) => state.flatContentLayers[id]?.settings.cellId === toCellId,
      );
      const abs = siblings.length
        ? table.childIds.indexOf(siblings[0])
        : table.childIds.length;
      table.childIds.splice(abs, 0, ...promoteRootIds);
      if (content !== undefined) {
        const tableData = table.settings.tableData as
          | Array<{ id: string; content: string }>
          | undefined;
        const cell = tableData?.find((c) => c.id === toCellId);
        if (cell) cell.content = content;
      } else if (priorContent !== undefined) {
        // content is "" on promote; prefer explicit content when present.
        const tableData = table.settings.tableData as
          | Array<{ id: string; content: string }>
          | undefined;
        const cell = tableData?.find((c) => c.id === toCellId);
        if (cell) cell.content = "";
      }
    }
  }

  applyInCellElementMove(state, { elementId, toContainerId, toCellId, index });
  if (!payload.broadcast) state.activeEntityId = elementId;
};

// Undo of a Tables 2.0 clipboard paste: drop the pasted roots and their
// subtrees, restore the pre-paste grid snapshot wholesale, and restore
// anything the safety prune removed.
const handlePasteCellsUndoCreateAction = (
  state: StoryStateType,
  payload: CreatePasteCellsPayloadType,
) => {
  const { entities, rootIds, tableEntityId, prior, prunedEntities } = payload;
  const table = state.flatContentLayers[tableEntityId];
  if (!table) return;
  const rootIdSet = new Set(rootIds);
  table.childIds = table.childIds.filter((id) => !rootIdSet.has(id));
  for (const id in entities) delete state.flatContentLayers[id];
  if (prunedEntities) {
    state.flatContentLayers = { ...state.flatContentLayers, ...prunedEntities };
    for (const [id, index] of Object.entries(payload.prunedIndexes ?? {})) {
      table.childIds.splice(Math.min(index, table.childIds.length), 0, id);
    }
  }
  // Restore the target cells' subtrees a span-replace discarded. Must come
  // after the prunedEntities restore above — prunedIndexes were captured on
  // an array that still had these removed subtrees absent, so this splice
  // has to layer on top, not the other way round.
  if (payload.removedEntities) {
    state.flatContentLayers = {
      ...state.flatContentLayers,
      ...payload.removedEntities,
    };
    for (const [id, index] of Object.entries(payload.removedIndexes ?? {})) {
      table.childIds.splice(Math.min(index, table.childIds.length), 0, id);
    }
  }
  Object.assign(table.settings, prior);
  if (!payload.broadcast) state.activeEntityId = payload.initialActiveId;
};

const handlePasteCellsRedoCreateAction = (
  state: StoryStateType,
  payload: CreatePasteCellsPayloadType,
) => {
  const { entities, rootIds, tableEntityId, next } = payload;
  const table = state.flatContentLayers[tableEntityId];
  if (!table) return;
  state.flatContentLayers = { ...state.flatContentLayers, ...entities };
  table.childIds.push(...rootIds);
  if (payload.removedEntities) {
    const removedIds = Object.keys(payload.removedEntities);
    const removedSet = new Set(removedIds);
    table.childIds = table.childIds.filter((id) => !removedSet.has(id));
    for (const id of removedIds) delete state.flatContentLayers[id];
  }
  if (payload.prunedEntities) {
    const prunedIds = Object.keys(payload.prunedEntities);
    const prunedSet = new Set(prunedIds);
    table.childIds = table.childIds.filter((id) => !prunedSet.has(id));
    for (const id of prunedIds) delete state.flatContentLayers[id];
  }
  Object.assign(table.settings, next);
  if (!payload.broadcast) state.activeEntityId = tableEntityId;
};

const handleNarratorDialogUndoCreateAction = (
  state: StoryStateType,
  payload: CreateGenericPayloadType,
) => {
  const { parentId, entities, index, initialRootId, initialEntities } = payload;
  if (!initialRootId || !initialEntities) return;
  const parent = state.flatContentLayers[parentId];
  if (!parent) return;
  parent.childIds[index] = initialRootId;
  for (const entityId in entities) {
    delete state.flatContentLayers[entityId];
  }
  state.flatContentLayers = { ...state.flatContentLayers, ...initialEntities };
  if (!payload.broadcast) state.activeEntityId = initialRootId;
};

const handleUndoDesignSystemAction = (
  state: StoryStateType,
  payload: DesignSystemPayloadType,
) => {
  const story = state.story;
  if (!story) return;
  const { designSystem, originalEntities } = payload.current;

  Object.keys(originalEntities).forEach((entityId) => {
    const entity = state.flatContentLayers[entityId];
    if (!entity) return;
    Object.assign(entity.settings, originalEntities[entityId]);
  });

  Object.assign(story.settings.designSystemV2, designSystem);
  updateDesignSystemRoot(designSystem as TDesignSystemRoot);
};

const handleUndoChangeEntityLayoutAction = (
  state: StoryStateType,
  payload: ChangeEntityLayoutPayloadType,
) => {
  const { parentId, original, changed, type } = payload.payload;
  handleChangeEntityLayout(parentId, changed, original, state, type);
};

// ========== UNDO ==========

// ========== REDO ==========
const handleRedoEditAction = (
  state: StoryStateType,
  payload: EditPayloadType,
) => {
  const entity = state.flatContentLayers[payload.id];
  if (!entity) return;
  const { childCurrentSettings, payload: editPayload } = payload;
  const { full, children, data } = editPayload;
  if (full) {
    entity.settings = data;
  } else {
    for (const key in data) {
      entity.settings[key] = data[key];
    }
  }

  updateTabsDefault(payload.id, state.flatContentLayers);

  // Redo re-applies the tableData rewrite, so re-prune the same children.
  if (payload.prunedChildren) {
    pruneOrphanedCellChildren(state, payload.id);
  }

  if (children) {
    for (const childId in childCurrentSettings) {
      const child = state.flatContentLayers[childId];
      if (!child) {
        console.error(
          `Child with id ${childId} not found in flatContentLayers`,
        );
        continue;
      }
      for (const key in childCurrentSettings[childId]) {
        child.settings[key] = data[key];
      }
    }
  }
};

const handleRedoEditManyAction = (
  state: StoryStateType,
  payload: EditManyPayloadType,
) => {
  const { payload: data } = payload;
  Object.entries(data).forEach(([id, payload]) => {
    const entity = state.flatContentLayers[id];
    if (!entity) return;
    Object.keys(payload).forEach((key) => {
      entity.settings[key] = payload[key];
    });
  });
  // Mirror of the undo restore above: re-apply the NEXT fs object so redo
  // re-applies both halves of the atomic frame.
  if (payload.fsPatch && state.story) {
    state.story.settings.fs = payload.fsPatch.next;
  }
};

const handleRedoDeleteAction = (
  state: StoryStateType,
  payload: DeletePayloadType,
) => {
  const { rootId, index, entities, updatedActiveId } = payload;
  const root = state.flatContentLayers[rootId];
  const parent = state.flatContentLayers[root.parentId || ""];
  if (parent) {
    parent.childIds.splice(index, 1);
    if (!payload.broadcast) state.activeEntityId = updatedActiveId;
  }

  for (const entityId in entities) {
    delete state.flatContentLayers[entityId];
  }
};

const handleRedoReorderAction = (
  state: StoryStateType,
  payload: ReorderPayloadType,
) => {
  const { id, currentParentId, updatedParentId, currentIndex, updatedIndex } =
    payload.payload;

  const entity = state.flatContentLayers[id];
  if (!entity) return;
  const parent = state.flatContentLayers[currentParentId];
  if (!parent) return;
  if (currentParentId === updatedParentId) {
    parent.childIds.splice(currentIndex, 1);
    parent.childIds.splice(updatedIndex, 0, id);
  } else {
    const updatedParent = state.flatContentLayers[updatedParentId];
    if (!updatedParent) return;
    parent.childIds.splice(currentIndex, 1);
    updatedParent.childIds.splice(updatedIndex, 0, id);
    entity.parentId = updatedParentId;
  }
};

const handleRedoCreateAction = (
  state: StoryStateType,
  payload: CreatePayloadType,
) => {
  const { parentId, entities, index, rootId, type } = payload;
  if (type === "narrator-dialog")
    return handleNarratorDialogRedoCreateAction(
      state,
      payload as CreateGenericPayloadType,
    );
  if (type === "insert-into-cell")
    return handleInsertIntoCellRedoCreateAction(
      state,
      payload as CreateInsertIntoCellPayloadType,
    );
  if (type === "move-in-cell-element")
    return handleMoveInCellElementRedoCreateAction(
      state,
      payload as CreateMoveInCellElementPayloadType,
    );
  if (type === "paste-cells")
    return handlePasteCellsRedoCreateAction(
      state,
      payload as CreatePasteCellsPayloadType,
    );
  const parent = state.flatContentLayers[parentId];
  if (!parent) return;
  parent.childIds.splice(index, 0, rootId);
  state.flatContentLayers = { ...state.flatContentLayers, ...entities };
  if (!payload.broadcast) {
    state.activeEntityId = rootId;
    if (parentId === "root") state.activeThumbnailId = rootId;
  }
};

const handleNarratorDialogRedoCreateAction = (
  state: StoryStateType,
  payload: CreateGenericPayloadType,
) => {
  const { parentId, entities, index, rootId, initialEntities } = payload;
  if (!initialEntities) return;
  const parent = state.flatContentLayers[parentId];
  if (!parent) return;
  parent.childIds[index] = rootId;
  for (const entityId in initialEntities) {
    delete state.flatContentLayers[entityId];
  }
  state.flatContentLayers = { ...state.flatContentLayers, ...entities };
  if (!payload.broadcast) state.activeEntityId = rootId;
};

const handleRedoDesignSystemAction = (
  state: StoryStateType,
  payload: DesignSystemPayloadType,
) => {
  const { design, entities } = payload.payload;
  const story = state.story;
  if (!story) return;
  handleDesignSystem({
    state,
    design,
    entities,
  });

  updateDesignSystemRoot(design as TDesignSystemRoot);
};

const handleRedoChangeEntityLayoutAction = (
  state: StoryStateType,
  payload: ChangeEntityLayoutPayloadType,
) => {
  const { parentId, original, changed, type } = payload.payload;
  handleChangeEntityLayout(parentId, original, changed, state, type);
};

// ========== REDO ==========
